import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";

import { getSettings, putSettings, type Settings } from "@/lib/api";

/** ProcessSettingsForm collects every admin-only knob into a single
 *  card on the /admin page. Fields mirror the 19 ProcessWritableKeys
 *  in internal/config/tenant.go. Reads + writes via GET/PUT
 *  /api/settings — no new endpoint needed. Validation ranges match
 *  the server-side ApplySettings clamps. */
export function ProcessSettingsForm() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [ttlSeconds, setTtlSeconds] = useState("");
  const [trackPollSeconds, setTrackPollSeconds] = useState("");
  const [scannerEnabled, setScannerEnabled] = useState(false);
  const [scannerTickDelayMs, setScannerTickDelayMs] = useState("");
  const [scannerRefreshHours, setScannerRefreshHours] = useState("");
  const [scannerMinHitScore, setScannerMinHitScore] = useState("");
  const [scannerIncludeETFs, setScannerIncludeETFs] = useState(false);
  const [scannerRegimeGate, setScannerRegimeGate] = useState(true);
  const [scannerRegimeBenchmark, setScannerRegimeBenchmark] = useState("");
  const [scannerRegimeSMA, setScannerRegimeSMA] = useState("");
  const [qualityMinRR, setQualityMinRR] = useState("");
  const [qualityMaxAtrPct, setQualityMaxAtrPct] = useState("");
  const [qualityMaxTrailing60d, setQualityMaxTrailing60d] = useState("");
  const [qualityMinFloatMcap, setQualityMinFloatMcap] = useState("");
  const [qualityMaxDollarVolPctFloat, setQualityMaxDollarVolPctFloat] = useState("");
  const [qualityEarningsBufferDays, setQualityEarningsBufferDays] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void getSettings()
      .then((s) => {
        setSettings(s);
        setTtlSeconds(String(s.CacheTTLSeconds));
        setTrackPollSeconds(String(s.TrackPollSeconds));
        setScannerEnabled(s.ScannerEnabled);
        setScannerTickDelayMs(String(s.ScannerTickDelayMs));
        setScannerRefreshHours(String(s.ScannerUniverseRefreshHours));
        setScannerMinHitScore(String(s.ScannerMinHitScore));
        setScannerIncludeETFs(s.ScannerIncludeETFs);
        setScannerRegimeGate(s.ScannerRegimeGate);
        setScannerRegimeBenchmark(s.ScannerRegimeBenchmark);
        setScannerRegimeSMA(String(s.ScannerRegimeSMA));
        setQualityMinRR(String(s.QualityMinRR));
        setQualityMaxAtrPct(String(s.QualityMaxAtrPct));
        setQualityMaxTrailing60d(String(s.QualityMaxTrailing60DReturn));
        setQualityMinFloatMcap(String(s.QualityMinFloatMcap));
        setQualityMaxDollarVolPctFloat(String(s.QualityMaxDollarVolPctFloat));
        setQualityEarningsBufferDays(String(s.QualityEarningsBufferDays));
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const pollN = Number(trackPollSeconds);
      if (!Number.isFinite(pollN) || pollN < 60 || pollN > 3600) {
        throw new Error("Tracker refresh must be between 60 and 3600 seconds");
      }
      const tickDelay = Number(scannerTickDelayMs);
      if (!Number.isFinite(tickDelay) || tickDelay < 500 || tickDelay > 30000) {
        throw new Error("Scanner tick delay must be between 500 and 30000 ms");
      }
      const refreshHours = Number(scannerRefreshHours);
      if (!Number.isFinite(refreshHours) || refreshHours < 1 || refreshHours > 720) {
        throw new Error("Scanner universe refresh must be between 1 and 720 hours");
      }
      const minHit = Number(scannerMinHitScore);
      if (!Number.isFinite(minHit) || minHit < -7 || minHit > 7) {
        throw new Error("Scanner min hit score must be between -7 and 7");
      }
      const regimeSMA = Number(scannerRegimeSMA);
      if (!Number.isFinite(regimeSMA) || regimeSMA < 1 || regimeSMA > 500) {
        throw new Error("Scanner regime SMA must be between 1 and 500");
      }
      if (scannerRegimeBenchmark.trim() === "") {
        throw new Error("Scanner regime benchmark must not be empty");
      }
      const qMinRR = Number(qualityMinRR);
      if (!Number.isFinite(qMinRR) || qMinRR < 0 || qMinRR > 10) {
        throw new Error("Quality min R:R must be in [0, 10]");
      }
      const qMaxAtr = Number(qualityMaxAtrPct);
      if (!Number.isFinite(qMaxAtr) || qMaxAtr < 0 || qMaxAtr > 1) {
        throw new Error("Quality max ATR% must be in [0, 1]");
      }
      const qMaxTrail = Number(qualityMaxTrailing60d);
      if (!Number.isFinite(qMaxTrail) || qMaxTrail < 0 || qMaxTrail > 10) {
        throw new Error("Quality max 60d return must be in [0, 10]");
      }
      const qMinFloat = Number(qualityMinFloatMcap);
      if (!Number.isFinite(qMinFloat) || qMinFloat < 0) {
        throw new Error("Quality min float market cap must be ≥ 0");
      }
      const qMaxDvPctFloat = Number(qualityMaxDollarVolPctFloat);
      if (!Number.isFinite(qMaxDvPctFloat) || qMaxDvPctFloat < 0 || qMaxDvPctFloat > 1) {
        throw new Error("Quality max daily vol / float must be in [0, 1]");
      }
      const qEarnDays = Number(qualityEarningsBufferDays);
      if (!Number.isFinite(qEarnDays) || qEarnDays < 0 || qEarnDays > 30) {
        throw new Error("Quality earnings buffer must be in [0, 30] days");
      }
      const payload: Record<string, unknown> = {
        cache_ttl_seconds: Number(ttlSeconds),
        track_poll_seconds: pollN,
        scanner_enabled: scannerEnabled,
        scanner_tick_delay_ms: tickDelay,
        scanner_universe_refresh_hours: refreshHours,
        scanner_min_hit_score: minHit,
        scanner_include_etfs: scannerIncludeETFs,
        scanner_regime_gate: scannerRegimeGate,
        scanner_regime_benchmark: scannerRegimeBenchmark.trim(),
        scanner_regime_sma: regimeSMA,
        quality_min_rr: qMinRR,
        quality_max_atr_pct: qMaxAtr,
        quality_max_trailing_60d_return: qMaxTrail,
        quality_min_float_mcap: qMinFloat,
        quality_max_dollar_vol_pct_float: qMaxDvPctFloat,
        quality_earnings_buffer_days: qEarnDays,
      };
      const updated = await putSettings(payload);
      setSettings(updated);
      setNotice("Saved. Scanner re-evaluates every cached row on quality-gate changes.");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Process settings</CardTitle>
        <p className="text-xs text-muted-foreground">
          Shared by every tenant. Edits take effect live — no restart
          needed. Quality-gate changes re-stamp every cached scanner
          row through <code>ReapplyHitGate</code>.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-sm text-rose-500">{error}</p>}
        {notice && !error && (
          <p className="text-xs text-emerald-500">{notice}</p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="ttl" className="text-xs">
              Cache TTL (seconds)
            </Label>
            <Input
              id="ttl"
              type="number"
              value={ttlSeconds}
              onChange={(e) => setTtlSeconds(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="track-poll" className="text-xs">
              Tracker refresh (seconds, 60–3600)
            </Label>
            <Input
              id="track-poll"
              type="number"
              min={60}
              max={3600}
              step={30}
              value={trackPollSeconds}
              onChange={(e) => setTrackPollSeconds(e.target.value)}
            />
          </div>
        </div>

        <Separator />
        <p className="text-xs font-semibold text-muted-foreground">
          Background scanner
        </p>
        <div className="flex items-center gap-2">
          <Switch
            id="scanner-enabled"
            checked={scannerEnabled}
            onCheckedChange={setScannerEnabled}
          />
          <Label htmlFor="scanner-enabled" className="text-xs">
            Enable scanner
          </Label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="scanner-tick" className="text-xs">
              Tick delay (ms, 500–30000)
            </Label>
            <Input
              id="scanner-tick"
              type="number"
              min={500}
              max={30000}
              step={100}
              value={scannerTickDelayMs}
              onChange={(e) => setScannerTickDelayMs(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="scanner-refresh" className="text-xs">
              Universe refresh (hours, 1–720)
            </Label>
            <Input
              id="scanner-refresh"
              type="number"
              min={1}
              max={720}
              value={scannerRefreshHours}
              onChange={(e) => setScannerRefreshHours(e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="scanner-hit" className="text-xs">
              Min OverallScore (-5..5)
            </Label>
            <Input
              id="scanner-hit"
              type="number"
              min={-5}
              max={5}
              value={scannerMinHitScore}
              onChange={(e) => setScannerMinHitScore(e.target.value)}
            />
          </div>
          <div className="flex items-end gap-2">
            <Switch
              id="scanner-etfs"
              checked={scannerIncludeETFs}
              onCheckedChange={setScannerIncludeETFs}
            />
            <Label htmlFor="scanner-etfs" className="text-xs">
              Include ETFs in universe
            </Label>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-end gap-2">
            <Switch
              id="scanner-regime"
              checked={scannerRegimeGate}
              onCheckedChange={setScannerRegimeGate}
            />
            <Label htmlFor="scanner-regime" className="text-xs">
              Regime gate (suppress in bear markets)
            </Label>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="scanner-regime-bmk" className="text-xs">
              Regime benchmark
            </Label>
            <Input
              id="scanner-regime-bmk"
              type="text"
              value={scannerRegimeBenchmark}
              onChange={(e) => setScannerRegimeBenchmark(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="scanner-regime-sma" className="text-xs">
              Regime SMA (1..500)
            </Label>
            <Input
              id="scanner-regime-sma"
              type="number"
              min={1}
              max={500}
              value={scannerRegimeSMA}
              onChange={(e) => setScannerRegimeSMA(e.target.value)}
            />
          </div>
        </div>

        <Separator />
        <p className="text-xs font-semibold text-muted-foreground">
          Quality gates
        </p>
        <p className="text-xs text-muted-foreground">
          Zero on any field disables that check. Edits re-stamp every
          cached scanner row immediately.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="quality-min-rr" className="text-xs">
              Min R:R
            </Label>
            <Input
              id="quality-min-rr"
              type="number"
              min={0}
              max={10}
              step={0.1}
              value={qualityMinRR}
              onChange={(e) => setQualityMinRR(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="quality-max-atr" className="text-xs">
              Max ATR%
            </Label>
            <Input
              id="quality-max-atr"
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={qualityMaxAtrPct}
              onChange={(e) => setQualityMaxAtrPct(e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="quality-max-trailing" className="text-xs">
              Max 60d return
            </Label>
            <Input
              id="quality-max-trailing"
              type="number"
              min={0}
              max={10}
              step={0.05}
              value={qualityMaxTrailing60d}
              onChange={(e) => setQualityMaxTrailing60d(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="quality-earnings-days" className="text-xs">
              Earnings buffer (days)
            </Label>
            <Input
              id="quality-earnings-days"
              type="number"
              min={0}
              max={30}
              step={1}
              value={qualityEarningsBufferDays}
              onChange={(e) => setQualityEarningsBufferDays(e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="quality-float-mcap" className="text-xs">
              Min float mcap (USD)
            </Label>
            <Input
              id="quality-float-mcap"
              type="number"
              min={0}
              step={1000000}
              value={qualityMinFloatMcap}
              onChange={(e) => setQualityMinFloatMcap(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="quality-vol-float" className="text-xs">
              Max daily vol / float
            </Label>
            <Input
              id="quality-vol-float"
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={qualityMaxDollarVolPctFloat}
              onChange={(e) => setQualityMaxDollarVolPctFloat(e.target.value)}
            />
          </div>
        </div>

        <div className="pt-2">
          <Button disabled={saving || !settings} onClick={save}>
            {saving ? "Saving…" : "Save process settings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
